// One Mission Panel card's capability switches as Solid state (#6766, epic #6552; prototype
// cap / askYes / askNo `:2611-2622`). The rules are `./machine-capabilities.ts`'s; this
// holds the client's facts (answers waiting for a fresh read, sentences, "Saved", the open
// confirm), reads the page's ONE flood guard, and writes through `setMachineRole`. Made by the
// card beside its job and epic controls (`./card-controls.ts`), and reached by its
// Capabilities zone through `./card-controls-context.ts`; on only for an admin on an own-org
// machine, as they are.
//
// DERIVED FROM THE NEWEST FRESH READ. An answer is stamped in the page's order when it lands
// (`orderStamp`), before the re-read it triggers starts, and held until a read later in that
// order arrives (`settleCaps`): that read wins, whatever it shows. "Saved" and the "{name}
// off, saved" announcement come only from a read that CONFIRMS the change; one that
// disagrees shows what it read, with no success wording.
//
// BOUNDED. The answers, sentences and "Saved" marks are keyed by role (nine at most), and the
// one timer per "Saved" mark is cleared when it is replaced and when the card unmounts.

import type { AgentModeKey, MachineRoleView } from '@developerz/domain';
import { createEffect, createSignal, on, onCleanup, untrack } from 'solid-js';
import { t } from '../i18n';
import { setMachineRole } from './agent-modes';
import type { CardControls } from './card-controls';
import type { UtilBox } from './fleetUtilization';
import { isSaving, lockedSeconds, type PageGuard, wantedValue } from './flood-guard-context';
import { refusalText } from './job-controls';
import {
  CAP_NAME_KEY,
  type CapAnswer,
  type CapClientState,
  type CapOutcome,
  type CapRefusal,
  type CapSettled,
  type CapSwitchView,
  type CapZoneView,
  capabilityView,
  capControlKey,
  queueCapability,
  SAVED_MS,
  settleCaps,
  storedValue,
} from './machine-capabilities';
import { orderStamp } from './read-order';

/** An open "Turn off {name}?" confirm. */
export type CapAsk = { readonly key: AgentModeKey; readonly jobs: number };

export type CapabilitySwitchesInput = {
  readonly page: PageGuard;
  readonly box: () => UtilBox;
  readonly token: string | null;
  /** Re-read the fleet: every write is followed by one, whatever it answered. */
  readonly onWritten: () => void;
  /** When the fetch behind the shown read started, in the page's order (`read_order`). */
  readonly readOrder: () => number;
  /** The machine reads offline: every switch is disabled. */
  readonly offline: () => boolean;
  /** The card's shared state: its live region, its Stop confirm, its red flash. */
  readonly card: CardControls;
};

const withEntry = <K, V>(m: ReadonlyMap<K, V>, k: K, v: V): ReadonlyMap<K, V> =>
  new Map(m).set(k, v);
const without = <K, V>(m: ReadonlyMap<K, V>, k: K): ReadonlyMap<K, V> => {
  if (!m.has(k)) return m;
  const next = new Map(m);
  next.delete(k);
  return next;
};

/** The sentence a send's ending leaves under its switch, or null (a 2xx, a 429, a 404). */
function outcomeText(o: CapOutcome): string | null {
  if (o.kind === 'refused') {
    return o.code === 'last_role_on' ? t('fleet.card.caps.last_on') : refusalText(o.code, t);
  }
  return o.kind === 'failed' ? t('fleet.machines.ctl_failed') : null;
}

/** What a confirming read announces: off, "will turn off after this job", or on. */
function savedLive(role: MachineRoleView): string {
  const name = t(CAP_NAME_KEY[role.key]);
  if (role.machine_enabled) return t('fleet.card.caps.on_saved', { name });
  return role.running > 0
    ? t('fleet.card.caps.finishing_saved', { name })
    : t('fleet.card.caps.off_saved', { name });
}

/** The answers, sentences and "Saved" marks one card holds. */
function createCapState(input: CapabilitySwitchesInput) {
  const [answers, setAnswers] = createSignal<ReadonlyMap<AgentModeKey, CapAnswer>>(new Map());
  const [refusals, setRefusals] = createSignal<ReadonlyMap<AgentModeKey, CapRefusal>>(new Map());
  const [saved, setSaved] = createSignal<ReadonlySet<AgentModeKey>>(new Set());
  const savedTimers = new Map<AgentModeKey, ReturnType<typeof setTimeout>>();
  onCleanup(() => {
    for (const h of savedTimers.values()) clearTimeout(h);
    savedTimers.clear();
  });
  const markSaved = (key: AgentModeKey): void => {
    const prior = savedTimers.get(key);
    if (prior !== undefined) clearTimeout(prior);
    setSaved((s) => new Set(s).add(key));
    savedTimers.set(
      key,
      setTimeout(() => {
        savedTimers.delete(key);
        setSaved((s) => {
          const next = new Set(s);
          next.delete(key);
          return next;
        });
      }, SAVED_MS),
    );
  };
  const settle = (s: CapSettled): void => {
    if (s.kind === 'confirmed') {
      markSaved(s.key);
      input.card.setLive(savedLive(s.role));
      return;
    }
    const name = t(CAP_NAME_KEY[s.key]);
    const text = s.role.machine_enabled
      ? t('fleet.card.caps.read_shows_on', { name })
      : t('fleet.card.caps.read_shows_off', { name });
    setRefusals((m) =>
      withEntry(m, s.key, { text, order: input.readOrder(), enabled: s.role.machine_enabled }),
    );
  };
  // Each read the card is handed settles what is older than it (`settleCaps`).
  createEffect(
    on([input.readOrder, () => input.box().roles], ([readOrder, roles]) => {
      untrack(() => {
        const next = settleCaps(answers(), refusals(), readOrder, roles ?? []);
        if (next.answers !== answers()) setAnswers(next.answers);
        if (next.refusals !== refusals()) setRefusals(next.refusals);
        for (const s of next.settled) settle(s);
      });
    }),
  );
  return { answers, setAnswers, refusals, setRefusals, saved };
}

/** One card's capability switches. Made by the card, under the page's guard. */
export function createCapabilitySwitches(input: CapabilitySwitchesInput) {
  const { page, card } = input;
  const st = createCapState(input);
  const [ask, setAsk] = createSignal<CapAsk | null>(null);
  // One confirm per card at a time: a Stop confirm opening closes this one.
  createEffect(
    on(card.ask, (a) => {
      if (a !== null) setAsk(null);
    }),
  );
  const roles = (): readonly MachineRoleView[] | null => input.box().roles ?? null;
  const client = (key: AgentModeKey): CapClientState => {
    const ck = capControlKey(input.box().id, key);
    return {
      wanted: wantedValue(page, ck) as boolean | undefined,
      saving: isSaving(page, ck),
      answer: st.answers().get(key),
      saved: st.saved().has(key),
      refusal: st.refusals().get(key),
    };
  };
  const view = (): CapZoneView | null => {
    const rs = roles();
    if (rs === null) return null;
    return capabilityView({
      roles: rs,
      offline: input.offline(),
      locked: lockedSeconds(page) > 0,
      client,
    });
  };
  const switchOf = (key: AgentModeKey): CapSwitchView | undefined =>
    view()?.switches.find((s) => s.key === key);

  const onOutcome = (key: AgentModeKey, o: CapOutcome): void => {
    // Stamped BEFORE the re-read starts, so that read is later in the page's order.
    const order = orderStamp();
    if (o.kind === 'answered') {
      st.setAnswers((m) => withEntry(m, key, { value: o.value, order }));
    } else {
      const text = outcomeText(o);
      const role = roles()?.find((r) => r.key === key);
      if (text !== null && role !== undefined) {
        st.setRefusals((m) => withEntry(m, key, { text, order, enabled: role.machine_enabled }));
      }
    }
    input.onWritten();
  };

  /** Every gate a click passes, re-run at the moment it acts. Null: nothing to do. */
  const gate = (key: AgentModeKey): CapSwitchView | null => {
    const sw = switchOf(key);
    if (sw === undefined || sw.disabled || sw.busy) return null;
    if (sw.hold) {
      // Locked: the page's one sentence for a change made while locked (#6753).
      card.setLive(t('fleet.machines.ctl_model_locked'));
      return null;
    }
    return sw;
  };

  const send = (key: AgentModeKey, to: boolean): void => {
    const role = roles()?.find((r) => r.key === key);
    if (role === undefined) return;
    st.setRefusals((m) => without(m, key));
    queueCapability({
      guard: page.guard,
      boxId: input.box().id,
      key,
      from: storedValue(role, client(key)),
      to,
      door: (enabled) => setMachineRole(input.box().id, key, enabled, input.token),
      onOutcome: (o) => onOutcome(key, o),
    });
  };

  return {
    view,
    ask,
    /** A click: on (from off or "then off"), or off, asking first when the role runs jobs. */
    toggle(key: AgentModeKey): void {
      const sw = gate(key);
      if (sw === null) return;
      if (!sw.checked || sw.finishing) {
        send(key, true);
        return;
      }
      if (sw.running && !sw.pending) {
        card.setAsk(null);
        setAsk({ key, jobs: sw.jobs });
        card.setFlash(true);
        card.later(900, () => card.setFlash(false));
        return;
      }
      send(key, false);
    },
    /** "Turn off after this job": the same PUT, through every gate again. */
    confirm(): void {
      const a = ask();
      setAsk(null);
      if (a === null) return;
      const sw = gate(a.key);
      if (sw === null || !sw.checked || sw.finishing) return;
      send(a.key, false);
    },
    /** "Keep on", or Escape: nothing is sent. */
    cancel(): void {
      setAsk(null);
    },
  };
}

export type CapabilitySwitches = ReturnType<typeof createCapabilitySwitches>;
